import api from '@/shared/utils/apiConfig';
import type { ImportCsvPreviewResult, ImportInspectResult } from '@/shared/import-export';
import {
  downloadBlob,
  filenameFromContentDisposition,
} from '@/shared/import-export';

export interface AppointmentCustomerSourceRef {
  _id?: string;
  id?: string;
  name?: string;
  code?: string;
}

export interface AppointmentPatientRef {
  _id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  patientId?: string;
  customerSource?: AppointmentCustomerSourceRef | string;
}

export interface AppointmentLeadRef {
  _id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  phone?: string;
  email?: string;
  status?: string;
  customerSource?: AppointmentCustomerSourceRef | string;
}

export interface AppointmentDentistRef {
  _id: string;
  firstName?: string;
  lastName?: string;
  specialization?: string;
  designation?: string;
  isDoctor?: boolean;
}

export interface AppointmentClinicRef {
  _id: string;
  name?: string;
  address?: unknown;
  branchCode?: string;
  shortName?: string;
}

export interface AppointmentTypeRef {
  _id?: string;
  id?: string;
  name?: string;
  code?: string;
  color?: string;
  defaultDuration?: number;
}

export interface AppointmentChairRef {
  _id?: string;
  id?: string;
  name?: string;
  code?: string;
}

export interface AppointmentRecord {
  _id: string;
  appointmentCode?: string;
  patient?: string | AppointmentPatientRef;
  lead?: string | AppointmentLeadRef;
  dentist?: string | AppointmentDentistRef;
  clinic?: string | AppointmentClinicRef;
  appointmentDate: string;
  endTime?: string;
  duration?: number;
  status: string;
  visitKind?: 'new' | 'follow_up';
  appointmentType?: string | AppointmentTypeRef;
  chair?: string | AppointmentChairRef | null;
  notes?: string;
  reasonForVisit?: string;
  treatment?: string | { _id: string; name?: string };
  cancelReason?: string;
  arrivedAt?: string;
  treatmentPlan?: string | { _id: string; name?: string; planCode?: string };
  followUpRequired?: boolean;
  followUpDate?: string;
  checkedInBy?:
    | string
    | {
        _id?: string;
        id?: string;
        username?: string;
        name?: string;
        email?: string;
      };
  cancelledBy?: string | { id: string; name?: string };
  /** Người tạo lịch. */
  createdBy?: { id: string; name: string };
  /** Người sửa lần cuối. */
  updatedBy?: { id: string; name: string };
  createdAt?: string;
  updatedAt?: string;
}

export interface Doctor {
  _id: string;
  firstName: string;
  lastName: string;
  specialization?: string;
  designation?: string;
  isDoctor?: boolean;
}

export interface AppointmentExportParams {
  search?: string;
  status?: string;
  appointmentType?: string;
  dentist?: string;
  patientId?: string;
  clinic?: string;
  startDate?: string;
  endDate?: string;
}

export interface AppointmentParams {
  patientId?: string;
  leadId?: string;
  status?: string;
  limit?: number;
  page?: number;
  [key: string]: any;
}

export interface CreateAppointmentPayload {
  patient?: string;
  lead?: string;
  clinic: string;
  dentist: string;
  appointmentDate: string | Date;
  endTime?: string | Date;
  duration: number;
  appointmentType: string;
  visitKind: 'new' | 'follow_up';
  reasonForVisit?: string;
  notes?: string;
  status?: string;
  chair?: string | null;
  treatment?: string | null;
  confirmWarnings?: boolean;
}

export type UpdateAppointmentPayload = Partial<CreateAppointmentPayload> & {
  cancelReason?: string;
  confirmWarnings?: boolean;
};

export interface UpdateAppointmentStatusPayload {
  status: string;
  cancelReason?: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  total?: number;
  count?: number;
  message?: string;
  [key: string]: any;
}

export const appointmentAPI = {
  getAppointments: async (
    params: AppointmentParams = {}
  ): Promise<ApiResponse<AppointmentRecord[]>> => {
    const response = await api.get('/appointments', { params });
    return response.data;
  },

  suggestVisitKind: async (params: {
    patientId?: string;
    leadId?: string;
    excludeAppointmentId?: string;
  }): Promise<'new' | 'follow_up'> => {
    const response = await api.get<ApiResponse<{ visitKind: 'new' | 'follow_up' }>>(
      '/appointments/visit-kind-suggestion',
      { params },
    );
    return response.data.data?.visitKind === 'follow_up' ? 'follow_up' : 'new';
  },

  getAppointment: async (id: string): Promise<ApiResponse<AppointmentRecord>> => {
    const response = await api.get(`/appointments/${id}`);
    return response.data;
  },

  createAppointment: async (
    appointmentData: CreateAppointmentPayload
  ): Promise<ApiResponse<AppointmentRecord>> => {
    const response = await api.post('/appointments', appointmentData);
    return response.data;
  },

  updateAppointment: async (
    id: string,
    appointmentData: UpdateAppointmentPayload
  ): Promise<ApiResponse<AppointmentRecord>> => {
    const response = await api.put(`/appointments/${id}`, appointmentData);
    return response.data;
  },

  updateStatus: async (
    id: string,
    payload: UpdateAppointmentStatusPayload
  ): Promise<ApiResponse<AppointmentRecord>> => {
    const response = await api.put(`/appointments/${id}`, payload);
    return response.data;
  },

  deleteAppointment: async (id: string): Promise<ApiResponse> => {
    const response = await api.delete(`/appointments/${id}`);
    return response.data;
  },

  getPatientAppointments: async (
    patientId: string
  ): Promise<ApiResponse<AppointmentRecord[]>> => {
    const response = await api.get('/appointments', {
      params: { patientId, limit: 100 },
      headers: { 'X-Clinic-Scope': 'all' },
    });
    return response.data;
  },

  getUpcomingAppointments: async (): Promise<ApiResponse<AppointmentRecord[]>> => {
    const response = await api.get('/appointments', {
      params: { status: 'scheduled', limit: 100 },
      headers: { 'X-Clinic-Scope': 'all' },
    });
    return response.data;
  },

  exportCsv: async (params: AppointmentExportParams = {}): Promise<void> => {
    const response = await api.get('/appointments/export', {
      params,
      responseType: 'blob',
    });
    const filename = filenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined,
      'appointments.csv'
    );
    downloadBlob(response.data as Blob, filename);
  },

  downloadImportTemplate: async (): Promise<void> => {
    const response = await api.get('/appointments/import/template', {
      responseType: 'blob',
    });
    const filename = filenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined,
      'appointment-import-template.csv'
    );
    downloadBlob(response.data as Blob, filename);
  },

  inspectImport: async (
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<ApiResponse<ImportInspectResult>> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/appointments/import/inspect', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
        onProgress(percent);
      },
    });
    return response.data;
  },

  importCsv: async (
    file: File,
    dryRun: boolean,
    onProgress?: (percent: number) => void,
    columnMapping?: Record<string, string>
  ): Promise<ApiResponse<ImportCsvPreviewResult>> => {
    const formData = new FormData();
    formData.append('file', file);
    if (columnMapping && Object.keys(columnMapping).length > 0) {
      formData.append('columnMapping', JSON.stringify(columnMapping));
    }
    const response = await api.post('/appointments/import', formData, {
      params: { dryRun: dryRun ? 'true' : 'false' },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
        onProgress(percent);
      },
    });
    return response.data;
  },

  /** Range-scoped calendar feed (no pagination; org + clinicScope on server). */
  getCalendarAppointments: async (params: {
    startDate: string;
    endDate: string;
    dentist?: string;
    clinic?: string;
  }): Promise<ApiResponse<AppointmentRecord[]>> => {
    const response = await api.get('/appointments/calendar', { params });
    return response.data;
  },
};
